(() => {
  if (customElements.get('pp-plant-options')) return;
  class PlantOptions extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = document.getElementById(this.getAttribute('form'));
      // The element may connect before the form is parsed; the deferred script
      // and variant replacements normally run after the full form exists.
      this.info = this.closest('product-info');
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.potDetails = this.querySelector('[data-pp-responsive-details="pot"]');
      const applyBreakpoint = () => { if (this.potDetails) this.potDetails.open = !this.mobile.matches; };
      applyBreakpoint();
      this.mobile.addEventListener('change', applyBreakpoint, options);
      this.potDetails?.addEventListener('toggle', () => this.updateArrows(), options);
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.matches('input[name="bundles"]')) {
          if (input.checked) {
            this.querySelectorAll('input[name="bundles"]:checked').forEach(other => {
              if (other !== input && ((input.dataset.group === 'pot' && other.dataset.group === 'pot') || other.value === input.value)) other.checked = false;
            });
          }
          this.update();
        }
      }, options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-pp-open-delivery]')) {
          const delivery = document.getElementById('pp-plant-delivery');
          if (delivery) delivery.open = true;
        }
        const scrollButton = event.target.closest('[data-pot-scroll]');
        if (scrollButton) {
          const track = this.querySelector('.pp-option-track');
          track?.scrollBy({ left: Number(scrollButton.dataset.potScroll) * track.clientWidth * .85, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
        if (event.target.closest('[data-no-pot]')) {
          this.querySelectorAll('[data-group="pot"]').forEach(input => { input.checked = false; });
          this.update();
        }
      }, options);
      this.info?.addEventListener('input', () => this.update(), options);
      this.info?.addEventListener('change', () => this.update(), options);
      this.form?.addEventListener('variant:change', () => this.update(), options);
      document.addEventListener('ajaxProduct:added', (event) => {
        const payload = event.detail?.product;
        const added = payload?.items || (payload ? [payload] : []);
        const matchesPlant = added.some(item => String(item.variant_id ?? item.id) === this.dataset.variantId);
        if (matchesPlant && this.form?.querySelector('[name="add"][aria-busy="true"]')) {
          this.querySelectorAll('input[name="bundles"]').forEach(input => { input.checked = false; });
          this.update();
        }
      }, options);
      this.track = this.querySelector('.pp-option-track');
      this.track?.addEventListener('scroll', () => this.updateArrows(), options);
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      if (this.track) this.resizeObserver.observe(this.track);
      this.updateArrows();
      this.update();
      document.dispatchEvent(new CustomEvent('pp:pots-updated', { detail: { productId: this.dataset.productId } }));
    }
    updateArrows() {
      if (!this.track) return;
      const previous = this.querySelector('[data-pot-scroll="-1"]');
      const next = this.querySelector('[data-pot-scroll="1"]');
      if (previous) previous.disabled = this.track.scrollLeft <= 1;
      if (next) next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }
    update() {
      const selected = [...this.querySelectorAll('input[name="bundles"]:checked:not(:disabled)')];
      const quantityInput = this.form?.querySelector('[name="quantity"]') || this.info?.querySelector('[name="quantity"]');
      const quantity = Math.max(1, Number(quantityInput?.value) || 1);
      // Matches the original ProductForm: plant quantity is independent of
      // accessory quantity; each accessory uses its Shopify minimum quantity.
      const items = selected.map(input => ({ id: input.value, price: Number(input.dataset.price), quantity: Number(input.dataset.min) || 1 }));
      const total = Number(this.dataset.basePrice) * quantity + items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const target = this.querySelector('[data-total]');
      const formattedTotal = new Intl.NumberFormat(this.dataset.locale || 'da', { style: 'currency', currency: this.dataset.currency || 'DKK', minimumFractionDigits: total % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(total / 100);
      if (target) target.textContent = formattedTotal;
      const sticky = this.closest('.pp-plant-product')?.querySelector('[data-pp-sticky-total]');
      if (sticky) sticky.textContent = `I alt: ${formattedTotal}`;
      const none = this.querySelector('[data-no-pot]');
      none?.setAttribute('aria-pressed', String(!selected.some(input => input.dataset.group === 'pot')));
      const selectedPot = selected.find(input => input.dataset.group === 'pot');
      const selectedLabel = this.querySelector('[data-pot-selection]');
      if (selectedLabel) {
        selectedLabel.hidden = !selectedPot;
        selectedLabel.textContent = selectedPot ? (selectedPot.dataset.summary || selectedPot.dataset.title) : '';
      }
      const note = this.querySelector('[data-selection-note]');
      if (note) {
        const mismatchedQuantity = selected.some(input => (Number(input.dataset.min) || 1) !== quantity);
        note.hidden = !mismatchedQuantity;
        note.textContent = mismatchedQuantity ? `${quantity} planter + ${selected.map(input => `${Number(input.dataset.min) || 1} × ${input.dataset.title}`).join(' · ')}` : '';
      }
      // Feed all groups to the theme's existing price listeners at once.
      this.form?.dispatchEvent(new CustomEvent('bundle:change', { detail: { items } }));
    }
  }
  customElements.define('pp-plant-options', PlantOptions);
})();

(() => {
  if (!customElements.get('pp-accessory-card')) {
    customElements.define('pp-accessory-card', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        document.addEventListener('ajaxProduct:added', (event) => {
          const button = this.querySelector('[name="add"]');
          if (!button?.matches('[aria-busy="true"]')) return;
          const payload = event.detail?.product;
          const added = payload?.items || (payload ? [payload] : []);
          const variantId = this.querySelector('input[name="id"]')?.value;
          if (!added.some(item => String(item.variant_id ?? item.id) === variantId)) return;
          const text = button.querySelector('.btn-text > span');
          const status = this.querySelector('[role="status"]');
          if (text) text.textContent = 'Tilføjet ✓';
          if (status) status.textContent = 'Tilbehøret er tilføjet til kurven';
          clearTimeout(this.timer);
          this.timer = setTimeout(() => {
            if (text && this.isConnected) text.textContent = 'Tilføj';
          }, 2500);
        }, { signal: this.controller.signal });
      }
      disconnectedCallback() {
        this.controller?.abort();
        clearTimeout(this.timer);
      }
    });
  }
  if (!customElements.get('pp-gallery-status')) {
    customElements.define('pp-gallery-status', class extends HTMLElement {
      connectedCallback() {
        const gallery = this.closest('.product__gallery-container');
        if (!gallery) return;
        const update = () => {
          const thumbnails = [...gallery.querySelectorAll('.product__thumbnail')].filter(item => !item.hidden && !item.closest('[hidden]'));
          const active = thumbnails.findIndex(item => item.getAttribute('aria-current') === 'true');
          const text = active >= 0 && thumbnails.length > 1 ? `${active + 1} / ${thumbnails.length}` : '';
          if (this.textContent !== text) this.textContent = text;
          if (this.hidden !== !text) this.hidden = !text;
        };
        this.observer = new MutationObserver(update);
        this.observer.observe(gallery, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-current', 'hidden'] });
        update();
      }
      disconnectedCallback() { this.observer?.disconnect(); }
    });
  }
})();

(() => {
  if (customElements.get('pp-responsive-disclosure')) return;
  customElements.define('pp-responsive-disclosure', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const details = this.querySelector('details');
      const mobile = window.matchMedia('(max-width: 767px)');
      const apply = () => { if (details) details.open = !mobile.matches; };
      apply();
      mobile.addEventListener('change', apply, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
})();
